<template>
  <DetailCard :loading="loading" :error="errorMessage">
    <template #heading>
      <span class="pi pi-map-marker"></span>
      {{ $t(`messages.detail.map.micro`) }}
    </template>
    <template #controls>
      <label v-if="!loading" for="detail-map-micro-switch" class="mb-0">
        {{ $t(`messages.detail.map.onoff`) }}
      </label>
      <ToggleSwitch v-if="!loading" id="detail-map-micro-switch" v-model="status" />
      <MapHelpTooltip v-if="!loading" size="sm" variant="secondary" />
    </template>

    <div id="detail-micro-map" class="detail-map">
      <MapComponent
        :center="center"
        :extent="mapview.extent"
        :zoom="zoom"
        :minZoom="mapview.minZoom"
        :maxZoom="mapview.maxZoom"
        :projection="mapview.projection"
        :baseLayers="mapview.baseLayers"
        :infoLayers="infoLayers"
        :overlay="overlay"
        :zoomToFeatures="false"
        :zoomControl="true"
        :twoFingerPanZoom="true"
        @update="exportMap"
        ref="mapRef"
      />
      <div class="map-cover" v-if="!status"></div>
    </div>
  </DetailCard>
</template>

<script setup>
import centroid from '@turf/centroid'
import { useDetailMapHelpers } from '@/composables/useDetailMapHelpers'
import { useMicroMacroDetailMap } from '@/composables/useMicroMacroDetailMap'
import {
  DETAIL_MACRO_MAP_FEATURE_LAYER,
  DETAIL_MICRO_MAP_FEATURE_LAYER,
  DETAIL_MICRO_MAP_INFO_LAYERS,
} from '@/constants'
import { createGeoJsonFeature } from '@/services/map/FeatureService'
import MapComponent from '../../map/MapComponent.vue'
import MapHelpTooltip from '../../map/MapHelpTooltip.vue'
import DetailCard from '../DetailCard.vue'

const createMicroMapFeatures = (
  id,
  geometry,
  featureLayerFn,
  overlayRef,
  centerRef,
  zoomRef,
  isLandParcel,
) => {
  const point = centroid(geometry[0])
  point.properties.id = id

  if (isLandParcel) {
    // A house icon makes no sense for a land parcel — outline the cadastre
    // with the macro layer's red stroke, but keep the micro zoom level.
    const { findLayer } = useDetailMapHelpers()
    const layer = findLayer(DETAIL_MACRO_MAP_FEATURE_LAYER)
    geometry.forEach(item => {
      layer.features.push(createGeoJsonFeature(item, { id }))
    })
    overlayRef.value = layer
  } else {
    const layer = featureLayerFn()
    layer.features.push(point)
    overlayRef.value = layer
  }

  // Set center to the centroid coordinates and zoom closer
  centerRef.value = point.geometry.coordinates
  zoomRef.value = 10
}

const {
  mapRef,
  loading,
  status,
  center,
  zoom,
  overlay,
  infoLayers,
  mapview,
  exportMap,
  errorMessage,
} = useMicroMacroDetailMap({
  mapType: 'micro',
  featureLayerId: DETAIL_MICRO_MAP_FEATURE_LAYER,
  infoLayersIds: DETAIL_MICRO_MAP_INFO_LAYERS,
  createMapFeaturesFunction: createMicroMapFeatures,
})

defineExpose({ exportMap })
</script>

<style scoped lang="postcss">
  .detail-map {
    position: relative;
    height: 600px;
    width: 100%;
    margin: 0;

    .map-cover {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      backdrop-filter: blur(5px) grayscale(80%);
      z-index: 3;
    }
  }
</style>
